import axios from 'axios';
import { tokenService } from '@/services/TokenService';
import router from '@/router';
import EventBus from '../EventBus';

class HttpService {
  mockBaseUrl = '/api-mock/';
  baseUrl = process.env.VUE_APP_API_URL;

  getMock(url: string, params: any = {}, options: any = {}) {
    return axios.get(`${this.mockBaseUrl}${url}`, { params, headers: this.getHeaders() }).then(
      response => {
        return response.data;
      },
      error => this.handleErrors(error, options)
    );
  }

  get(url: string, params: any = {}, options: any = {}) {
    return axios.get(`${this.baseUrl}${url}`, { params, headers: this.getHeaders() }).then(
      response => {
        return response.data;
      },
      error => this.handleErrors(error, options)
    );
  }

  post(url: string, data: any, params: any = {}, options: any = {}) {
    return axios.post(`${this.baseUrl}${url}`, data, { params, headers: this.getHeaders() }).then(
      response => {
        return response.data;
      },
      error => this.handleErrors(error, options)
    );
  }

  postWithHeaders(url: string, data: any, params: any = {}, options: any = {}) {
    return axios.post(`${this.baseUrl}${url}`, data, { params, headers: this.getHeaders() }).then(
      response => {
        return response;
      },
      error => this.handleErrors(error, options)
    );
  }

  getWithHeadersBinary(url: string, params: any = {}, options: any = {}) {
    return axios.get(`${this.baseUrl}${url}`, { params, headers: this.getHeaders(), responseType: 'arraybuffer' }).then(
      response => {
        return response;
      },
      error => this.handleErrors(error, options)
    );
  }

  postWithHeadersBinary(url: string, data: any, params: any = {}, options: any = {}) {
    return axios
      .post(`${this.baseUrl}${url}`, data, { params, headers: this.getHeaders(), responseType: 'arraybuffer' })
      .then(
        response => {
          return response;
        },
        error => this.handleErrors(error, options)
      );
  }

  postMultipart(url: string, data: any, params: any = {}, options: any = {}) {
    return axios.post(`${this.baseUrl}${url}`, data, { params, headers: this.getHeaders(true) }).then(
      response => {
        return response.data;
      },
      error => this.handleErrors(error, options)
    );
  }

  put(url: string, data: any, params: any = {}, options: any = {}) {
    return axios.put(`${this.baseUrl}${url}`, data, { params, headers: this.getHeaders() }).then(
      response => {
        return response.data;
      },
      error => this.handleErrors(error, options)
    );
  }

  delete(url: string, data: any, params: any = {}, options: any = {}) {
    return axios.delete(`${this.baseUrl}${url}`, { params, data, headers: this.getHeaders() }).then(
      response => {
        return response.data;
      },
      error => this.handleErrors(error, options)
    );
  }

  private handleErrors(error: any, options: any) {
    if (error && error.response && error.response.status === 401) {
      if (!(options && options.isAuthErrorSuppressed)) {
        tokenService.removeToken();
        router.push('/login').catch(() => {});
        throw error.response;
      }
    }
    if (error && error.response && error.response.data && error.response.data.error) {
      if (!(options && options.isErrorSuppressed)) {
        if (error.response.status === 403 && error.response.data.error === 'invalid_power_state') {
          const invalidPowerState = (window as any).VueGlobal.$i18n.t('error.invalid_power_state');
          EventBus.$emit('show-snackbar', invalidPowerState);
        } else if (error.response.status === 403 && error.response.data.error === 'invalid_state') {
          const invalidState = (window as any).VueGlobal.$i18n.t('error.invalid_state', {
            params: error.response.data.expected.join(','),
          });
          EventBus.$emit('show-snackbar', invalidState);
        } else {
          EventBus.$emit('show-snackbar', this.formatErrorMessage(error.response.data.error));
        }
      }
    } else {
      let status = error && error.response && error.response.status ? error.response.status : 0;
      switch (status) {
        case 401:
          if (!(options && options.isAuthErrorSuppressed)) {
            EventBus.$emit('show-snackbar', 'An authorization error has occurred');
          }
          break;
        case 404:
          if (!(options && options.isNotFoundErrorSuppressed)) {
            EventBus.$emit('show-snackbar', 'Server error: 404 not found');
          }
          break;
        default:
          if (error.response.status !== 403 && error.response.data.errors !== 'inactive_workspace_exists') {
            EventBus.$emit('show-snackbar', `Server error occurred (code: ${status})`);
          }
      }
    }
    throw error.response ? error.response : { code: 0, data: {} };
  }

  private formatErrorMessage(errors: any) {
    let errorHtml = '';
    try {
      if (errors && typeof errors === 'string') {
        return (window as any).VueGlobal.$i18n.t(errors);
      }
      for (let error in errors) {
        if (errors.hasOwnProperty(error)) {
          for (let i = 0; i < errors[error].length; i++) {
            errorHtml += (window as any).VueGlobal.$i18n.t(
              `error.${error}.${errors[error][i].error}`,
              errors[error][i]
            );
          }
        }
      }
    } catch (e) {
      errorHtml += '[Translation error]';
    }
    return errorHtml;
  }

  private getHeaders(isMultipart: boolean = false) {
    let headers: any = {
      'Content-Type': isMultipart ? 'multipart/form-data' : 'application/json',
    };

    const token = tokenService.getToken();

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }
}

export const httpService = new HttpService();
